/**
 * Survey types — single source for the Public Site enquiry form and Portal
 * Survey records (ADR-0001 "Survey types", ADR-0009).
 *
 * Owner-confirmed (2026-09-28): management, refurbishment & demolition,
 * reinspection, sampling, and management with part refurbishment (ADR-0010).
 * All use the same material assessment scoring except sampling, which only
 * hides it (the scoring function has no type branch).
 */

/** Portal Survey type values — match the `survey_type` Postgres enum. */
export const PORTAL_SURVEY_TYPES = [
  "management",
  "refurbishment_demolition",
  "reinspection",
  "sampling",
  "management_part_rd",
] as const;

export type PortalSurveyType = (typeof PORTAL_SURVEY_TYPES)[number];

type PortalSurveyTypeCopy = {
  /** Short label for selects and lists. */
  label: string;
  /** Sentence-case label for PDF rows and running headers. */
  pdfLabel: string;
  /** Title-case label for the cover and PDF document title. */
  pdfTitle: string;
  /** Issue-page wording: "This report is issued as the … record". */
  recordNoun: string;
  /** One line for the create-survey popup. */
  description: string;
  /** False hides material assessment inputs and PDF scoring (ADR-0010). */
  scoresMaterial: boolean;
  /** Editor label for `work_scope`; null hides the field. */
  scopeLabel: string | null;
  /** PDF "Survey information" row label for `work_scope`. */
  scopeRowLabel: string | null;
  /** Adds the intrusive / vacated-area limitations block to the PDF. */
  intrusiveLimitations: boolean;
};

export const PORTAL_SURVEY_TYPE_COPY: Record<PortalSurveyType, PortalSurveyTypeCopy> = {
  management: {
    label: "Management survey",
    pdfLabel: "Management asbestos survey",
    pdfTitle: "Management Asbestos Survey Report",
    recordNoun: "management survey",
    description:
      "Locate and assess asbestos that could be disturbed in normal occupation and maintenance.",
    scoresMaterial: true,
    scopeLabel: null,
    scopeRowLabel: null,
    intrusiveLimitations: false,
  },
  refurbishment_demolition: {
    label: "Refurbishment & demolition survey",
    pdfLabel: "Refurbishment & demolition asbestos survey",
    pdfTitle: "Refurbishment & Demolition Asbestos Survey Report",
    recordNoun: "refurbishment & demolition survey",
    description:
      "Intrusive survey of the areas affected by planned refurbishment or demolition works.",
    scoresMaterial: true,
    scopeLabel: "Work scope (area and planned works)",
    scopeRowLabel: "Work scope",
    intrusiveLimitations: true,
  },
  reinspection: {
    label: "Reinspection",
    pdfLabel: "Asbestos reinspection",
    pdfTitle: "Asbestos Reinspection Report",
    recordNoun: "reinspection",
    description:
      "Re-check the condition of asbestos recorded in a previous survey.",
    scoresMaterial: true,
    scopeLabel: null,
    scopeRowLabel: null,
    intrusiveLimitations: false,
  },
  sampling: {
    label: "Sampling survey",
    pdfLabel: "Asbestos sampling survey",
    pdfTitle: "Asbestos Sampling Survey Report",
    recordNoun: "sampling survey",
    description:
      "Targeted samples of specific materials only. No material assessment scores or full register.",
    scoresMaterial: false,
    scopeLabel: "Materials / areas to sample",
    scopeRowLabel: "Sampling scope",
    intrusiveLimitations: false,
  },
  management_part_rd: {
    label: "Management survey with part refurbishment",
    pdfLabel: "Management asbestos survey with part refurbishment",
    pdfTitle: "Management & Part Refurbishment Asbestos Survey Report",
    recordNoun: "management and part refurbishment survey",
    description:
      "Management survey of the whole premises plus an intrusive survey of named refurbishment areas.",
    scoresMaterial: true,
    scopeLabel: "Refurbishment areas (intrusive survey)",
    scopeRowLabel: "Refurbishment areas",
    intrusiveLimitations: true,
  },
};

export function isPortalSurveyType(value: unknown): value is PortalSurveyType {
  return (
    typeof value === "string" &&
    (PORTAL_SURVEY_TYPES as readonly string[]).includes(value)
  );
}

/** Snapshots from before ADR-0009 have no type: they were all management. */
export function portalSurveyTypeCopy(
  value: string | null | undefined,
): PortalSurveyTypeCopy {
  return PORTAL_SURVEY_TYPE_COPY[isPortalSurveyType(value) ? value : "management"];
}

/**
 * Public Site enquiry options. Kept as the legacy hyphenated values so
 * existing enquiry emails/tests stay stable; "not-sure" is enquiry-only.
 */
export const SURVEY_TYPE_OPTIONS = [
  { value: "management", label: PORTAL_SURVEY_TYPE_COPY.management.label },
  {
    value: "refurbishment-demolition",
    label: PORTAL_SURVEY_TYPE_COPY.refurbishment_demolition.label,
  },
  { value: "reinspection", label: PORTAL_SURVEY_TYPE_COPY.reinspection.label },
  { value: "not-sure", label: "Not sure — advise me" },
] as const;

export type SurveyTypeValue = (typeof SURVEY_TYPE_OPTIONS)[number]["value"];

export const SURVEY_TYPE_VALUES = SURVEY_TYPE_OPTIONS.map((o) => o.value) as [
  SurveyTypeValue,
  ...SurveyTypeValue[],
];

export function surveyTypeLabel(value: SurveyTypeValue): string {
  return SURVEY_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

/**
 * ADR-0009: the type is chosen before first Issue and then fixed, so every
 * ReportVersion of a Survey shares one type. An amendment (draft again with
 * currentVersion > 0) cannot change it.
 */
export function surveyTypeChangeProblem(
  current: PortalSurveyType,
  next: PortalSurveyType | undefined,
  currentVersion: number | null | undefined,
): string | null {
  if (next === undefined || next === current) return null;
  if ((currentVersion ?? 0) > 0) {
    return "Survey type is fixed once a Report Version has been issued";
  }
  return null;
}
